import React, {PureComponent, Fragment} from 'react';
import dateformat from 'dateformat';
import ViewForm from './ViewForm';
import {declOfNum} from '../../../helpers/string';
import {GridColumn} from '@atlaskit/page';
import Button from '@atlaskit/button';
import { EditViewContext } from './EditViewContext';
import DynamicTable from '@atlaskit/dynamic-table';
import Drawer from '@atlaskit/drawer';
import styled from 'styled-components';
import CommentIcon from '@atlaskit/icon/glyph/comment';
import Tooltip from '@atlaskit/tooltip';
import CarTOEditForm, {prepareTasks} from './CarTOEditForm';
import i18n from 'i18next';
import {useTranslation, Trans} from 'react-i18next';

const FORM_NAME = 'TO';
const FORM_NAME_NEXT_TO = 'TONew';

export default props => {
  const {t} = useTranslation('clients');
  return (
    <EditViewContext.Consumer>
      {({edit, permissions, onChangeEditMode}) => permissions.info && (
        <Fragment>
          {props.TOs && (
            <ViewForm
              formName={FORM_NAME}
              title='ТО'
              editMode={edit === FORM_NAME}
            >
              <CarTOLists
                edit={edit}
                onChangeEditMode={onChangeEditMode}
                t={t}
                {...props}
              />
            </ViewForm>
          )}
          {props.nextTO && (
            <ViewForm
              formName={FORM_NAME_NEXT_TO}
              editMode={edit === FORM_NAME_NEXT_TO}
            >
              {edit === FORM_NAME_NEXT_TO 
                ? <CarTOEditForm
                    data={props.nextTO}
                    fetching={props.fetching}
                    error={props.error}
                    dictionaries={props.dictionaries}
                    getDictionaries={props.getDictionaries}
                    onClose={onChangeEditMode(null)}
                    carId={props.carId}
                    onSave={props.onSave}
                  />
                : <NextTo
                    {...props.nextTO}
                    onComplete={onChangeEditMode(FORM_NAME_NEXT_TO)}
                  />
              }
            </ViewForm>
          )}
        </Fragment>
      )}
    </EditViewContext.Consumer>
  );
}

const Container = styled.div`
  margin-top: 16px;
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const NextTOMsg = styled.p`
  flex: 1;
`;

const NextTo = ({id, daysLeft, mileageLeft, onComplete}) => (
  <GridColumn medium={12}>
    <Container>
      <NextTOMsg>
        {i18n.t(
          id === 'ТО-0' ? 'clients:Next TO in km' : 'clients:Next TO in day(s) or km',
          {
            id,
            days: declOfNum(daysLeft, [i18n.t('day'), i18n.t('days2'), i18n.t('days')]),
            mileageLeft,
          }
        )}
      </NextTOMsg>
      <Button onClick={onComplete}>
        <Trans>
          Complete
        </Trans>
      </Button> 
    </Container>
  </GridColumn>
)


const List = styled.ul`
  list-style-type: none;
  padding: 0;
`;

const ListItem = styled.li`
  color: ${props => props.type && props.complete ?
    props.type === props.complete ? 'black' : 'red' : 'gray'
  }
  ${props => !props.complete && 'text-decoration: line-through'}
  display: flex;
  justify-content: center;
`;

const TaskTitle = styled.div`
  flex:1;
`;

const TaskType = styled.div`
  min-width: 48px;
`;

class CarTOLists extends PureComponent {
  constructor(props) {
    super(props);

    this.state = {
      toForEdit: null,
      details: null,
    };
  }
  componentDidMount() {
    const {
      dictionaries,
      getDictionaries,
    } = this.props;

    if (!dictionaries.TOTaskTypes.data) {
      (getDictionaries('TOTaskTypes'))();
    }
    if (!dictionaries.TOTasks.data) {
      (getDictionaries('TOTasks'))();
    }
  }

  onShowDetails = tasks => () => {
    this.setState({details: tasks})
  }

  onEdit = val => {
    this.setState(
      {toForEdit: val},
      this.props.onChangeEditMode(FORM_NAME)
    );
  }

  render () {
    const {
      fetching,
      error,
      edit,
      dictionaries,
      getDictionaries,
      onChangeEditMode,
      onChange,
      TOs,
      carId,
      onSave,
      t,
    } = this.props;

    const {
      toForEdit,
      details
    } = this.state;

    if (edit === FORM_NAME && toForEdit) {
      return (
        <CarTOEditForm
          data={toForEdit}
          fetching={fetching}
          error={error}
          dictionaries={dictionaries}
          getDictionaries={getDictionaries}
          onClose={onChangeEditMode(null)}
          onChange={onChange}
          carId={carId}
          onSave={onSave}
        />
      );
    }

    return (
      <Fragment>
        <GridColumn medium={12}>
          <br/>
          <DynamicTable
            head={HEAD(t)}
            rows={buildRows(TOs, this.onShowDetails, this.onEdit)}
            rowsPerPage={undefined}
            defaultPage={1}
          />
        </GridColumn>
  
        <Drawer
          onClose={this.onShowDetails(null)}
          isOpen={!!details}
          width="wide"
        >
          {details && (
          <div style={{marginRight: 24, paddingBottom: 24}}>
            <h1>{i18n.t('clients:Regular')} {details.id}</h1>
            <span style={{fontSize: 12}}>
              {i18n.t('clients:Abbr in table')}
            </span>
            {dictionaries.TOTasks.data && prepareTasks(details.id, dictionaries.TOTasks.data, details.tasks).map((group, index) => (
              <Fragment key={`task_group_${index}`}>
                <h4>{group.title}</h4>
                <List>
                  {group.tasks && group.tasks.map((task, j) => (
                    <ListItem
                      key={`task_group_${index}_${j}`}
                      type={task.type}
                      complete={task.complete}
                    >
                      <TaskTitle>
                        {task.title}
                      </TaskTitle>
                      <TaskType>
                        {task.complete ? task.complete : '-'}
                        {task.type !== task.complete && ` / ${task.type}`}
                      </TaskType>
                    </ListItem>
                  ))}
                </List>
              </Fragment>
            ))}
          </div>
          )}
        </Drawer>
      </Fragment>
    )
  }
}

const HEAD = t => ({
  cells: [
    {
      key: 'comment',
      content: '',
      isSortable: false,
      width: '24px',
    },
    {
      key: 'id',
      content: t('clients:#'),
      isSortable: false,
      width: '30px',
    },
    {
      key: 'tasks',
      content: t('clients:Works'),
      isSortable: false,
      width: '30px',
    },
    {
      key: 'odometer',
      content: t('clients:Mileage'),
      isSortable: false,
      width: '80px',
    },
    {
      key: 'date',
      content: t('clients:Date'),
      isSortable: false,
      width: '140px',
    },
    {
      key: 'dealer',
      content: t('clients:Dealer'),
      isSortable: false,
      width: undefined,
    },
    {
      key: 'edit',
      content: '',
      isSortable: false,
      width: '40px',
    }
  ],
});

const buildRows = (data, showDetails, onEdit) => (
  data.map((item, index) => ({
      key: `row-${index}-${item.id}`,
      cells: [
        {
          content: item.comment && (
            <Tooltip content={item.comment}>
              <CommentIcon primaryColor='gray'/>
            </Tooltip>
          ),
        },
        {
          content: item.id,
        },
        {
          content: item.tasks ? (
            <Button
              onClick={showDetails(item)}
              spacing='compact'
            >
              {i18n.t('clients:Details')}
            </Button>
          ) : '-',
        },
        {
          content: item.odometer ? `${item.odometer} км` : '-',
        },
        {
          content: dateformat(item.date, 'dd.mm.yyyy'),
        },
        {
          content: item.dealer ? item.dealer.name : '-',
        },
        {
          content: (
            <Button
              onClick={() => onEdit(item)}
              spacing='compact'
            >
              <Trans>
                Edit
              </Trans>
            </Button>
          ),
        },
      ]
    })
  )
);
